import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Select } from "antd";
import { get } from "@/services/axiosUitils";
import { DropdownService, PtsProjectService } from "@/services/APIs";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const DAILY_CAPACITY = 8;
const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
type AllocationValue = number | "FREE" | "LEAVE";

interface DayWiseAllocationDate {
  date: string;
  allocated_hours: AllocationValue;
  remaining_hours: AllocationValue;
}

interface DayWiseAllocationWeek {
  week_label: string;
  week_start: string;
  week_end: string;
  dates: DayWiseAllocationDate[];
}

interface DayWiseAllocationEmployee {
  emp_id: string;
  emp_name: string;
  month_total_allocated_hours: number;
  week_wise: DayWiseAllocationWeek[];
}

interface DayWiseResourceAllocationResponse {
  selected_project_id: string;
  selected_emp_id: string;
  filters: {
    month: string;
    year: number;
  };
  range_start: string;
  range_end: string;
  total_allocation_hours: number;
  results: DayWiseAllocationEmployee[];
}

function getCurrentMonthName(): string {
  return MONTH_OPTIONS[new Date().getMonth()];
}

function shiftMonth(
  monthName: string,
  year: number,
  direction: -1 | 1
): { month: string; year: number } {
  const currentMonthIndex = MONTH_OPTIONS.indexOf(monthName);
  const nextDate = new Date(year, currentMonthIndex + direction, 1);
  return {
    month: MONTH_OPTIONS[nextDate.getMonth()],
    year: nextDate.getFullYear(),
  };
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? "")
    .join("");
}

function formatMonthTitle(month: string, year: number): string {
  return `${month} ${year}`;
}

function formatDayHeader(dateString: string): string {
  const date = new Date(dateString);
  const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
  return `${dayName} ${String(date.getDate()).padStart(2, "0")}`;
}

function formatWeekRange(week: DayWiseAllocationWeek): string {
  const start = new Date(week.week_start);
  const end = new Date(week.week_end);
  return `${start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })} - ${end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })}`;
}

function formatRangeLabel(startDate?: string, endDate?: string): string {
  if (!startDate || !endDate) {
    return "--";
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  return `${start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })} - ${end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
}

function getNumericHours(value: AllocationValue | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function isFreeValue(value: AllocationValue | null | undefined): boolean {
  return value === "FREE" || value === null || value === undefined;
}

function isLeaveValue(value: AllocationValue | null | undefined): boolean {
  return value === "LEAVE";
}

function getAllocationCardStyles(
  allocatedHours: number,
  remainingHours: number,
  isFree: boolean,
  isLeave: boolean
) {
  if (isLeave) {
    return {
      containerClass: "border border-[#f8eedc] bg-[#fffbf5]",
      hourTextClass: "text-[#eb790f]",
      remainingTextClass: "text-[#eb790f]",
      progressTrackClass: "bg-[#ede9fe]",
      usedBarClass: "bg-transparent",
      remainingBarClass: "bg-transparent",
    };
  }

  if (isFree) {
    return {
      containerClass: "border border-[#d7efcf] bg-[#f4ffef]",
      hourTextClass: "text-[#19a35b]",
      remainingTextClass: "text-[#67b87f]",
      progressTrackClass: "bg-[#dff5e4]",
      usedBarClass: "bg-transparent",
      remainingBarClass: "bg-transparent",
    };
  }

  if (remainingHours <= 0) {
    return {
      containerClass: "border border-[#f3c4c4] bg-[#fff1f1]",
      hourTextClass: "text-[#d14545]",
      remainingTextClass: "text-[#d14545]",
      progressTrackClass: "bg-[#ffdede]",
      usedBarClass: "bg-[#ef4444]",
      remainingBarClass: "bg-transparent",
    };
  }

  if (allocatedHours === 4 && remainingHours === 4) {
    return {
      containerClass: "border border-[#efe4b4] bg-[#fff8dc]",
      hourTextClass: "text-[#b0821e]",
      remainingTextClass: "text-[#7f8c9d]",
      progressTrackClass: "bg-[#f3ebc8]",
      usedBarClass: "bg-[#eab308]",
      remainingBarClass: "bg-[#84cc16]",
    };
  }

  if (allocatedHours === 0 && remainingHours >= 8) {
    return {
      containerClass: "border border-[#d7e7fb] bg-[#eef7ff]",
      hourTextClass: "text-[#3b82f6]",
      remainingTextClass: "text-[#5f88b6]",
      progressTrackClass: "bg-[#dbeafe]",
      usedBarClass: "bg-transparent",
      remainingBarClass: "bg-[#3b82f6]",
    };
  }

  return {
    containerClass: "border border-[#efe4b4] bg-[#fff8dc]",
    hourTextClass: "text-[#b0821e]",
    remainingTextClass: "text-[#7f8c9d]",
    progressTrackClass: "bg-[#dff5e4]",
    usedBarClass: "bg-[#4f8ff7]",
    remainingBarClass: "bg-[#20bf73]",
  };
}

function getLineWidth(hours: number): string {
  const safeHours = Math.max(0, Math.min(hours, DAILY_CAPACITY));
  return `${(safeHours / DAILY_CAPACITY) * 100}%`;
}

function getRemainingLineWidth(
  remainingHours: number,
  isFree: boolean
): string {
  if (isFree) {
    return "100%";
  }

  return getLineWidth(Math.max(remainingHours, 0));
}

const ResourceAllocation = () => {
  const [month, setMonth] = useState<string>(getCurrentMonthName());
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [employeeId, setEmployeeId] = useState<string>("all");
  const [projectId, setProjectId] = useState<string>("all");

  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data: allocationResponse, isLoading: allocationLoading } = useQuery({
    queryKey: [
      "day-wise-resource-allocation",
      month,
      year,
      employeeId,
      projectId,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        filters: JSON.stringify({ month, year }),
      };

      if (employeeId !== "all") {
        params.emp_id = employeeId;
      }

      if (projectId !== "all") {
        params.project_id = projectId;
      }

      return get<DayWiseResourceAllocationResponse>(
        "/pts_app/day-wise-resource-allocation/",
        { params }
      );
    },
    enabled: !!month && !!year,
  });

  const weeks = useMemo(() => {
    return allocationResponse?.results?.[0]?.week_wise ?? [];
  }, [allocationResponse?.results]);

  const allWorkingDates = useMemo(() => {
    return weeks.flatMap(week => week.dates.map(day => day.date));
  }, [weeks]);

  const employees = useMemo(() => {
    return allocationResponse?.results ?? [];
  }, [allocationResponse?.results]);

  const dayLookupByEmployee = useMemo(() => {
    const lookup = new Map<string, Map<string, DayWiseAllocationDate>>();

    employees.forEach(employee => {
      const dateMap = new Map<string, DayWiseAllocationDate>();
      employee.week_wise.forEach(week => {
        week.dates.forEach(day => {
          dateMap.set(day.date, day);
        });
      });
      lookup.set(employee.emp_id, dateMap);
    });

    return lookup;
  }, [employees]);

  const handlePrevMonth = () => {
    const next = shiftMonth(month, year, -1);
    setMonth(next.month);
    setYear(next.year);
  };

  const handleNextMonth = () => {
    const next = shiftMonth(month, year, 1);
    setMonth(next.month);
    setYear(next.year);
  };

  const handleMonthChange = (nextMonth: string) => {
    setMonth(nextMonth);
  };

  const handleYearChange = (nextYear: string) => {
    setYear(Number(nextYear));
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div>
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Resource Allocation
        </h1>
        <p className="text-sm text-[#627384]">
          Day-wise team capacity view for the selected month
        </p>
      </div>

      {/* <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border border-gray-200 bg-white">
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-[#627384]">Employees</p>
              <p className="mt-1 text-2xl font-bold text-[#111827]">
                {employees.length}
              </p>
            </div>
            <div className="rounded-xl bg-blue-50 p-3 text-blue-600">
              <Users className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 bg-white">
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-[#627384]">Total Allocation Hours</p>
              <p className="mt-1 text-2xl font-bold text-[#111827]">
                {allocationResponse?.total_allocation_hours ?? 0}h
              </p>
            </div>
            <div className="rounded-xl bg-violet-50 p-3 text-violet-600">
              <Clock3 className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 bg-white">
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-[#627384]">Month</p>
              <p className="mt-1 text-2xl font-bold text-[#111827]">
                {formatMonthTitle(month, year)}
              </p>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3 text-emerald-600">
              <Layers className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-5">
            <p className="text-sm text-[#627384]">Range</p>
            <p className="mt-1 text-sm font-semibold text-[#111827]">
              {formatRangeLabel(
                allocationResponse?.range_start,
                allocationResponse?.range_end
              )}
            </p>
          </CardContent>
        </Card>
      </div> */}

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-4">
            <div className="space-y-2">
              <Label htmlFor="resource-month" className="text-sm text-gray-700">
                Month
              </Label>
              <Select
                id="resource-month"
                allowClear
                value={month}
                onChange={value =>
                  handleMonthChange(value ?? getCurrentMonthName())
                }
                placeholder="Select month"
                className="h-[2rem] w-full rounded-xl"
                options={MONTH_OPTIONS.map(monthName => ({
                  value: monthName,
                  label: monthName,
                }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="resource-year" className="text-sm text-gray-700">
                Year
              </Label>
              <input
                type="number"
                id="resource-year"
                value={year}
                onChange={event => handleYearChange(event.target.value)}
                min="2000"
                max="2100"
                className="h-[2rem] w-full rounded-lg border bg-[#fafafa] px-3 text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="resource-employee"
                className="text-sm text-gray-700"
              >
                Employee
              </Label>
              <Select
                id="resource-employee"
                allowClear
                value={employeeId}
                onChange={value => setEmployeeId(value ?? "all")}
                placeholder="Select employee"
                className="h-[2rem] w-full rounded-xl"
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? "");
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                options={[
                  { value: "all", label: "All Employees" },
                  ...((employeeOptions ?? []).map(
                    (employee: { value: string; label: string }) => ({
                      value: employee.value,
                      label: employee.label,
                    })
                  ) ?? []),
                ]}
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="resource-project"
                className="text-sm text-gray-700"
              >
                Project
              </Label>
              <Select
                id="resource-project"
                allowClear
                value={projectId}
                onChange={value => setProjectId(value ?? "all")}
                placeholder="Select project"
                className="h-[2rem] w-full rounded-xl"
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? "");
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                options={[
                  { value: "all", label: "All Projects" },
                  ...((projectOptions ?? []).map(
                    (project: { value: string; label: string }) => ({
                      value: project.value,
                      label: project.label,
                    })
                  ) ?? []),
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between px-1">
        <Button
          type="button"
          variant="ghost"
          onClick={handlePrevMonth}
          className="flex items-center gap-1 text-sm text-[#1e3a5f]"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        <div className="text-center">
          <p className="text-sm font-semibold text-[#1e3a5f]">
            {formatMonthTitle(month, year)}
          </p>
          <p className="text-xs text-gray-500">
            {formatRangeLabel(
              allocationResponse?.range_start,
              allocationResponse?.range_end
            )}
          </p>
        </div>

        <Button
          type="button"
          variant="ghost"
          onClick={handleNextMonth}
          className="flex items-center gap-1 text-sm text-[#1e3a5f]"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardHeader className="p-3 pb-3 md:p-6">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <div className="bg-primary/10 flex h-6 w-6 items-center justify-center rounded">
              <Layers className="text-primary h-3.5 w-3.5" />
            </div>
            Day-wise Resource Allocation
            <Badge variant="outline" className="ml-1 text-[10px]">
              {employees.length} employee{employees.length === 1 ? "" : "s"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0 md:p-6">
          {allocationLoading ? (
            <div className="flex items-center justify-center py-14">
              <LoadingSpinner />
            </div>
          ) : employees.length === 0 || allWorkingDates.length === 0 ? (
            <div className="py-14 text-center text-sm text-gray-500">
              No resource allocation data found for the selected filters.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <Table>
                <TableHeader>
                  {weeks.length > 0 && (
                    <TableRow className="border-0 hover:bg-transparent">
                      <TableHead className="left-0 z-10 min-w-[220px] bg-white sm:sticky" />
                      {weeks.map(week => (
                        <TableHead
                          key={`${week.week_label}-${week.week_start}`}
                          colSpan={week.dates.length}
                          className="h-auto border-l border-r border-gray-200 bg-white py-3 text-center text-[10px] font-bold text-[#21324a]"
                        >
                          {week.week_label}
                          <span className="ml-1 font-normal text-gray-500">
                            ({formatWeekRange(week)})
                          </span>
                        </TableHead>
                      ))}
                      <TableHead className="min-w-[72px] text-center text-[10px] font-semibold">
                        Total
                      </TableHead>
                    </TableRow>
                  )}

                  <TableRow className="border-0 bg-white hover:bg-transparent">
                    <TableHead className="left-0 z-10 min-w-[220px] bg-white py-3 text-[10px] font-semibold text-gray-500 sm:sticky">
                      Employee
                    </TableHead>
                    {allWorkingDates.map((day, index) => {
                      const isWeekStart =
                        index > 0 &&
                        weeks.some(week => week.dates[0]?.date === day);

                      return (
                        <TableHead
                          key={day}
                          className={`min-w-[92px] whitespace-nowrap bg-white py-3 text-center text-[10px] font-medium text-gray-500 ${isWeekStart ? "border-l border-gray-200" : ""}`}
                        >
                          {formatDayHeader(day)}
                        </TableHead>
                      );
                    })}
                    <TableHead className="min-w-[72px] bg-white text-center text-[10px] font-semibold text-gray-500">
                      Total
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {employees.map(employee => (
                    <TableRow
                      key={employee.emp_id}
                      className="bg-white hover:bg-transparent"
                    >
                      <TableCell className="left-0 z-10 border-b border-gray-200 bg-white py-3 sm:sticky">
                        <div className="ms-2 flex items-center gap-3">
                          {/* <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" /> */}
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-[#f7f8fa] text-[10px] font-semibold text-gray-600">
                            {getInitials(employee.emp_name)}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-900">
                              {employee.emp_name}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              {employee.month_total_allocated_hours}h total
                            </p>
                          </div>
                        </div>
                      </TableCell>

                      {allWorkingDates.map((day, index) => {
                        const dayData = dayLookupByEmployee
                          .get(employee.emp_id)
                          ?.get(day);
                        const isLeave =
                          isLeaveValue(dayData?.allocated_hours) ||
                          isLeaveValue(dayData?.remaining_hours);
                        const allocatedHours = getNumericHours(
                          dayData?.allocated_hours
                        );
                        const remainingHours = getNumericHours(
                          dayData?.remaining_hours
                        );
                        const isFree = isFreeValue(dayData?.allocated_hours);
                        const cardStyles = getAllocationCardStyles(
                          allocatedHours,
                          remainingHours,
                          isFree,
                          isLeave
                        );
                        const isWeekStart =
                          index > 0 &&
                          weeks.some(week => week.dates[0]?.date === day);

                        return (
                          <TableCell
                            key={`${employee.emp_id}-${day}`}
                            className={`border-b border-gray-200 px-1.5 py-1.5 text-center align-top ${isWeekStart ? "border-l border-gray-200" : ""}`}
                          >
                            <div
                              className={`rounded-[14px] px-3 py-2 ${cardStyles.containerClass}`}
                            >
                              {!isFree && !isLeave && (
                                <div
                                  className={`mb-2 flex h-[5px] w-full overflow-hidden rounded-full ${cardStyles.progressTrackClass}`}
                                >
                                  <div
                                    className={`h-full rounded-full ${cardStyles.usedBarClass}`}
                                    style={{
                                      width: getLineWidth(allocatedHours),
                                    }}
                                  />
                                  <div
                                    className={`h-full rounded-full ${cardStyles.remainingBarClass}`}
                                    style={{
                                      width: getRemainingLineWidth(
                                        remainingHours,
                                        isFree
                                      ),
                                    }}
                                  />
                                </div>
                              )}
                              <div
                                className={`flex min-h-[20px] min-w-[56px] items-center justify-center text-[15px] font-semibold ${cardStyles.hourTextClass}`}
                                title={`${employee.emp_name} - ${day}`}
                              >
                                {isLeave
                                  ? "LEAVE"
                                  : isFree
                                    ? "Free"
                                    : `${allocatedHours}h`}
                              </div>
                              {!isFree && !isLeave && (
                                <span
                                  className={`block whitespace-nowrap text-center text-[11px] font-medium ${cardStyles.remainingTextClass}`}
                                >
                                  {remainingHours < 0
                                    ? `${Math.abs(remainingHours)}h Overload`
                                    : `${remainingHours}h left`}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        );
                      })}

                      <TableCell className="border-b border-gray-200 bg-white py-2 text-center">
                        <span className="text-xs font-bold text-[#21324a]">
                          {employee.month_total_allocated_hours}h
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResourceAllocation;
